import JobPassOnPaymentFees from './JobPassOnPaymentFees'
import { Alert, Button, Card, DatePicker, Form, Input, Select, Space, Spin, Statistic, Switch, Typography } from 'antd'
import { useMemo, useRef, useState } from 'react'
import { ProjectTypes } from '../../common/constants'
import { useDebouncedCallback } from '../../helpers/debounce'
import { useBudgetYears } from '../Budgets/budgetQueries'
import { useActiveUsersQuery } from '../Users/usersQueries'
import { useJobDetailsQuery, useUpdateJobDetailsMutation } from './jobsQueries'
import { UpdateJobDetailsRequest } from './jobsApi'
import { detailsSaveErrorMessage } from './jobNumberModel'
import {
  ClockOutRequirementOption,
  clockOutRequirementForRequest,
  parseSoldOn,
  soldOnAfterSoldToggle,
  soldOnForRequest,
  toClockOutRequirementOption,
} from './jobDetailsFormModel'
import { isValidJobName, jobNameMaxLength } from '../../domain/jobs/jobName'
import useFeatureFlag from '../../hooks/useFeatureFlag'
import { JOB_DETAILS_CLEANUP_FEATURE } from '../../domain/jobs/jobs'
import JobOperations from './JobOperations'
import JobDetailsSections, { CompletedDateField } from './JobDetailsSections'
import VSpace from '../common/VSpace'
import dayjs, { Dayjs } from 'dayjs'

type ProjectAnalysis = {
  estimated: { price: number }
  actual: { price: number }
  final: { price: number }
}

type Props = {
  jobId: number
  projectAnalysis?: ProjectAnalysis
  onPricingSaved?: () => void
  hasUnsavedChanges?: boolean
  onSave?: (saved: UpdateJobDetailsRequest) => void
  completedDate?: CompletedDateField
}

type FormValues = {
  no: string
  name: string
  budgetYear: number
  salesPersonEmail: string
  estimatorEmail: string
  schedulers: string[]
  schedulingNotes: string
  crewNotes: string
  sold: boolean
  lost: boolean
  active: boolean
  status: number | null
  estimateDate: Dayjs
  soldOn: Dayjs | null
  clockOutNotes: ClockOutRequirementOption
  clockOutPhoto: ClockOutRequirementOption
}

const JobDetailsFormForJob = ({ jobId, projectAnalysis, onSave, onPricingSaved, hasUnsavedChanges, completedDate }: Props) => {
  const [detailsPending, setDetailsPending] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [saveError, setSaveError] = useState<string>()
  const editRevision = useRef(0)
  const [form] = Form.useForm<FormValues>()
  const jobDetailsQuery = useJobDetailsQuery(jobId)
  const updateMutation = useUpdateJobDetailsMutation(jobId)
  const activeUsersQuery = useActiveUsersQuery()
  const budgetYearsQuery = useBudgetYears()
  const jobDetailsCleanup = useFeatureFlag(JOB_DETAILS_CLEANUP_FEATURE)
  const jobDetailsCleanupRef = useRef(jobDetailsCleanup)
  jobDetailsCleanupRef.current = jobDetailsCleanup

  const jobDetails = jobDetailsQuery.data
  const isProject = jobDetails?.type === ProjectTypes.Project
  const isMaintenanceOrTicket = jobDetails?.type === ProjectTypes.Maintenance || jobDetails?.type === ProjectTypes.Ticket

  const sold = Form.useWatch('sold', form)

  const userOptions = useMemo(() => {
    if (!activeUsersQuery.data) {
      return []
    }
    const users = Array.isArray(activeUsersQuery.data) ? activeUsersQuery.data : [activeUsersQuery.data]
    return users.map((u: { email: string; displayName: string }) => ({
      label: u.displayName,
      value: u.email
    }))
  }, [activeUsersQuery.data])

  const budgetYearOptions = useMemo(() => {
    if (!budgetYearsQuery.data) {
      return []
    }
    return budgetYearsQuery.data.map((year: number) => ({
      label: String(year),
      value: year
    }))
  }, [budgetYearsQuery.data])

  const initialValues = useMemo(() => {
    if (!jobDetails) {
      return undefined
    }
    return {
      no: jobDetails.no ?? '',
      name: jobDetails.name ?? '',
      budgetYear: jobDetails.budgetYear,
      salesPersonEmail: jobDetails.salesPersonEmail ?? '',
      estimatorEmail: jobDetails.estimatorEmail ?? '',
      schedulers: jobDetails.schedulers ? jobDetails.schedulers.split(',').filter(Boolean) : [],
      schedulingNotes: jobDetails.schedulingNotes ?? '',
      crewNotes: jobDetails.crewNotes ?? '',
      sold: jobDetails.sold ?? false,
      lost: jobDetails.lost ?? false,
      active: jobDetails.active ?? false,
      status: jobDetails.status ?? -1,
      estimateDate: jobDetails.estimateDate ? dayjs(jobDetails.estimateDate) : null,
      soldOn: parseSoldOn(jobDetails.soldOn),
      clockOutNotes: toClockOutRequirementOption(jobDetails.requireClockOutNotes),
      clockOutPhoto: toClockOutRequirementOption(jobDetails.requireClockOutPhoto),
    }
  }, [jobDetails])

  const buildRequest = (values: FormValues): UpdateJobDetailsRequest => {
    return {
      ...(jobDetailsCleanupRef.current ? { no: values.no?.trim() || undefined } : {}),
      name: values.name,
      budgetYear: values.budgetYear,
      salesPersonEmail: values.salesPersonEmail,
      estimatorEmail: values.estimatorEmail,
      schedulers: (values.schedulers ?? []).join(','),
      schedulingNotes: values.schedulingNotes,
      crewNotes: values.crewNotes,
      sold: values.sold,
      lost: values.lost,
      active: values.active,
      status: values.status === -1 ? null : values.status,
      estimateDate: values.estimateDate?.toISOString() ?? '',
      soldOn: jobDetailsCleanupRef.current ? soldOnForRequest(values.sold, values.soldOn) : null,
      requireClockOutNotes: clockOutRequirementForRequest(values.clockOutNotes, jobDetails?.requireClockOutNotes),
      requireClockOutPhoto: clockOutRequirementForRequest(values.clockOutPhoto, jobDetails?.requireClockOutPhoto),
    }
  }

  const saveDetails = () => {
    // With job-details-cleanup, getFieldsValue(true) includes fields that aren't on screen for this job
    // (Status on tickets and unsold projects, Estimator, Active), so they're saved as loaded instead of as
    // undefined, which the API writes as null. Without the flag the save is unchanged.
    const values: FormValues = jobDetailsCleanupRef.current ? form.getFieldsValue(true) : form.getFieldsValue()
    if (jobDetailsCleanupRef.current && !isValidJobName(values.name)) {
      form.validateFields(['name']).catch(() => undefined)
      return
    }
    setSaveFailed(false)
    const revision = editRevision.current
    updateMutation.mutate(buildRequest(values), { onSuccess: (_data, saved) => {
      onSave?.(saved)
      if (revision === editRevision.current) {
        setDetailsPending(false)
      }
    }, onError: error => {
      if (revision === editRevision.current) {
        setSaveFailed(true)
        setSaveError(detailsSaveErrorMessage(error))
      }
    } })
  }
  const debouncedSave = useDebouncedCallback(saveDetails, 2500)

  const handleSoldChange = (checked: boolean) => {
    form.setFields([{ name: 'soldOn', value: soldOnAfterSoldToggle(checked, form.getFieldValue('soldOn'), dayjs()) }])
    if (checked) {
      form.setFields([{ name: 'lost', value: false }])
    }
  }

  const handleLostChange = (checked: boolean) => {
    if (checked) {
      form.setFields([{ name: 'sold', value: false }])
    }
  }

  if (jobDetailsQuery.isLoading) {
    return <Spin />
  }

  if (!jobDetails) {
    return null
  }


  const pricingStatistics = projectAnalysis && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
    <Statistic title="Estimated Total" value={projectAnalysis.estimated?.price / 100} precision={2} prefix="$" />
    <Statistic title="Actual Price" value={projectAnalysis.actual?.price / 100} precision={2} prefix="$" />
    <Statistic title="Final Price" value={projectAnalysis.final?.price / 100} precision={2} prefix="$" />
  </div>

  const saveFailedAlert = saveFailed && <Alert type="error" message="Your job detail changes have not been saved." description={saveError}
    action={<Button disabled={updateMutation.isLoading} onClick={() => { debouncedSave.cancel(); saveDetails() }}>Retry Save</Button>} />

  const passOnPaymentFees = <JobPassOnPaymentFees jobId={jobId} onSaved={onPricingSaved}
    hasUnsavedChanges={hasUnsavedChanges || detailsPending || updateMutation.isLoading} />

  const jobOperations = <JobOperations jobId={jobId} jobName={jobDetails.name} jobType={jobDetails.type} sold={jobDetails.sold} />

  const formProps = {
    form,
    layout: 'vertical' as const,
    initialValues,
    onValuesChange: () => { editRevision.current += 1; setDetailsPending(true); debouncedSave() },
  }

  if (jobDetailsCleanup) {
    return <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <VSpace size="middle">
        {saveFailedAlert}
        <Form {...formProps}>
          <JobDetailsSections
            jobNumberRequired={!!jobDetails.no}
            isProject={isProject}
            isMaintenanceOrTicket={isMaintenanceOrTicket}
            sold={sold}
            userOptions={userOptions}
            budgetYearOptions={budgetYearOptions}
            onLostChange={handleLostChange}
            onSoldChange={handleSoldChange}
            jobOperations={jobOperations}
            pricing={projectAnalysis && <VSpace size="large">
              {pricingStatistics}
              {passOnPaymentFees}
            </VSpace>}
            completedDate={completedDate}
          />
        </Form>
        {!projectAnalysis && passOnPaymentFees}
      </VSpace>
    </div>
  }

  return <Card title="Job Details" extra={jobOperations} style={{ maxWidth: 1200, margin: '0 auto' }}>
    <Form {...formProps}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', gap: 32 }}>
          <Space>
            <Form.Item name="lost" valuePropName="checked" noStyle>
              <Switch onChange={handleLostChange} />
            </Form.Item>
            Lost
          </Space>
          <Space>
            <Form.Item name="sold" valuePropName="checked" noStyle>
              <Switch onChange={handleSoldChange} />
            </Form.Item>
            Sold{jobDetails.soldDate && ` (${dayjs(jobDetails.soldDate).format('MM/DD/YYYY')})`}
          </Space>
          {isMaintenanceOrTicket && <Space>
            <Form.Item name="active" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            Active
          </Space>}
        </div>

        {!isProject && <Form.Item name="estimateDate" hidden><input type="hidden" /></Form.Item>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Form.Item name="name" label="Name" style={{ gridColumn: 'span 2' }}>
            <Input maxLength={jobNameMaxLength} />
          </Form.Item>
          <Form.Item label="Number">
            <Typography.Text>{jobDetails.no ?? 'Pending'}</Typography.Text>
          </Form.Item>

          {isProject && <Form.Item name="estimateDate" label="Estimate Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>}
          <Form.Item name="salesPersonEmail" label="Salesperson">
            <Select options={userOptions} allowClear />
          </Form.Item>
          {isProject && <Form.Item name="estimatorEmail" label="Estimator">
            <Select options={userOptions} allowClear />
          </Form.Item>}

          <Form.Item name="budgetYear" label="MORS Budget">
            <Select options={budgetYearOptions} />
          </Form.Item>
          {isProject && sold && <Form.Item name="status" label="Status">
            <Select options={[
              { label: 'Unscheduled', value: -1 },
              { label: 'Scheduled', value: 1 },
              { label: 'Waiting on parts', value: 2 },
              { label: 'Completed', value: 3 },
            ]} />
          </Form.Item>}

        </div>

        {pricingStatistics}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Form.Item name="schedulers" label="Notify Schedulers">
            <Select
              mode="multiple"
              placeholder="Select schedulers to notify"
              options={userOptions}
              allowClear
            />
          </Form.Item>
        </div>
        <Form.Item name="schedulingNotes" label="Scheduling Notes">
          <Input.TextArea rows={5} />
        </Form.Item>

        <Form.Item name="crewNotes" label="Crew Notes">
          <Input.TextArea rows={5} />
        </Form.Item>

      </Space>
    </Form>
    {saveFailedAlert}
    {passOnPaymentFees}
  </Card>
}

const JobDetailsForm = (props: Props) => <JobDetailsFormForJob key={props.jobId} {...props} />

export default JobDetailsForm
